import React from 'react';
import Grid2 from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from '../../../lib/RechartsWrapper';
import { FarmCostRow } from '../Model/CostType';
import { SCOPE_COLORS } from '../Model/Constants';
import {
  fmtCompact,
  fmtFull,
  NOK,
  CurrencyConfig,
} from '../../../Components/Layout/SqlServer/Common/util';

interface FarmsTabProps {
  farmSummary: FarmCostRow[];
  currency?: CurrencyConfig;
  baseCurrency?: string;
}

const BARS: {
  key: keyof Pick<FarmCostRow, 'Dedicated' | 'PlanShared' | 'FarmShared'>;
  label: string;
  color: string;
}[] = [
  { key: 'Dedicated', label: 'Dedicated', color: SCOPE_COLORS['Dedicated'] },
  { key: 'PlanShared', label: 'PlanShared', color: SCOPE_COLORS['PlanShared'] },
  { key: 'FarmShared', label: 'FarmShared', color: SCOPE_COLORS['FarmShared'] },
];

const FarmsTab: React.FC<FarmsTabProps> = ({
  farmSummary,
  currency = NOK,
  baseCurrency = 'NOK',
}) => {
  const tooltipFmt = (v: unknown): [string] => [
    fmtFull(Number(v), currency, baseCurrency),
  ];

  const convertValue = (v: number): number => {
    if (baseCurrency === 'USD') return v * currency.rate;
    if (currency.code === 'NOK') return v;
    return v / 10.5;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Chart */}
      <Paper sx={{ p: 2.5 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'text.secondary',
            mb: 2,
          }}
        >
          Cost by Farm
        </Typography>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={farmSummary} barCategoryGap="30%">
            <XAxis
              dataKey="FarmId"
              tick={{ fill: '#6b7280', fontSize: 11 }}
              tickFormatter={(f: string) => `farm${f}`}
            />
            <YAxis
              tick={{ fill: '#6b7280', fontSize: 11 }}
              tickFormatter={(v: number) => {
                const converted = convertValue(v);
                return converted >= 1000
                  ? `${(converted / 1000).toFixed(0)}k`
                  : `${converted.toFixed(0)}`;
              }}
            />
            <Tooltip
              contentStyle={{
                background: '#111827',
                border: '1px solid #374151',
                borderRadius: 8,
                fontSize: 11,
              }}
              formatter={tooltipFmt}
            />
            <Legend wrapperStyle={{ fontSize: 11, color: '#9ca3af' }} />
            {BARS.map((b) => (
              <Bar
                key={b.key}
                dataKey={b.key}
                name={b.label}
                fill={b.color}
                radius={[3, 3, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* Cards */}
      <Grid2 container spacing={2}>
        {farmSummary.map((f) => (
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={f.FarmId}>
            <Paper sx={{ p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                  farm{f.FarmId}
                </Typography>
                <Chip
                  label={f.Environment}
                  size="small"
                  sx={{
                    fontSize: 10,
                    height: 20,
                    bgcolor:
                      f.Environment === 'Prod'
                        ? 'rgba(16,185,129,0.15)'
                        : 'rgba(245,158,11,0.15)',
                    color: f.Environment === 'Prod' ? '#6ee7b7' : '#fcd34d',
                    border: `1px solid ${f.Environment === 'Prod' ? 'rgba(16,185,129,0.3)' : 'rgba(245,158,11,0.3)'}`,
                  }}
                />
              </Box>

              <Grid2 container spacing={1} sx={{ mb: 2 }}>
                {BARS.map((b) => (
                  <Grid2 size={{ xs: 4 }} key={b.key}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'rgba(255,255,255,0.04)',
                        textAlign: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 9,
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          color: 'text.disabled',
                          mb: 0.25,
                        }}
                      >
                        {b.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontFamily: 'monospace',
                          fontWeight: 700,
                          color: b.color,
                        }}
                      >
                        {fmtCompact(f[b.key] as number, currency, baseCurrency)}
                      </Typography>
                    </Box>
                  </Grid2>
                ))}
              </Grid2>

              <Typography
                sx={{
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'text.disabled',
                  mb: 0.75,
                }}
              >
                Tenants
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {f.Tenants.map((t) => (
                  <Chip
                    key={t}
                    label={t}
                    size="small"
                    sx={{
                      fontSize: 9,
                      height: 18,
                      bgcolor: '#1f2937',
                      color: 'text.secondary',
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                ))}
              </Box>
            </Paper>
          </Grid2>
        ))}
      </Grid2>
    </Box>
  );
};

export default FarmsTab;
